import { z } from 'zod';

import { collectionPath, defineModelSpec, orderBy } from '@elara/firebolt/shared';
import { zFirestampOptional } from '@elara/firebolt/zod-types';

import { ParticipantSchema } from './schemas/ParticipantSchema.js';
import { id } from './schemas/zod-types.js';

/**
 * Auto-maintained fields used by the admin panel for fast list queries.
 * Populated at write time (booleans) and by Eventarc-driven worker handlers (counters/timestamps).
 * NEVER write to these from client code — they are consistency-managed by the platform.
 *
 * Counters are best-effort projections maintained via FieldValue.increment and can drift
 * (see server/src/admin/admin-counters.ts). The schema deliberately does NOT enforce
 * `.nonnegative()` — a drifted counter should degrade the admin number, not take the
 * whole trip document offline for client listeners and admin reads. Run
 * `server/scripts/backfill-admin-counters.ts` to repair drift.
 */
export const TripDerivedSchema = z.object({
  isOwnerAdmin: z.boolean().optional().describe('Trip owner has @elara.travel email; populated at create'),
  chatCount: z.number().int().optional().describe('Number of chats; maintained by worker (best-effort, may drift)'),
  itineraryItemCount: z.number().int().optional().describe('Number of itinerary items; maintained by worker (best-effort, may drift)'),
  shortlistItemCount: z.number().int().optional().describe('Number of shortlist items; maintained by worker (best-effort, may drift)'),
  messageCount: z.number().int().optional().describe('Total messages across all chats; maintained by worker (best-effort, may drift)'),
  lastMessageAt: zFirestampOptional().describe('Timestamp of most recent message in any chat; maintained by worker. Seeded to createdAt on trip create.'),
});
export type TripDerived = z.infer<typeof TripDerivedSchema>;

/** Schema for a single day's summary (generated title) in itinerary day summaries */
export const DaySummarySchema = z.object({
  date: z.string().describe('Date key (yyyy-MM-dd)'),
  title: z.string().describe('Generated day title'),
});
export type DaySummary = z.infer<typeof DaySummarySchema>;

/**
 * Public read-only share link for a trip's itinerary. Present only while sharing is on; deleting
 * the field revokes the link immediately, and re-enabling mints a fresh token so old links stay dead.
 * Written exclusively by the Enable/DisableTripShareLink flows. The token is a bearer credential for
 * the public projection served by `POST /api/share/itinerary` -- never log it or copy it elsewhere.
 */
export const TripShareLinkSchema = z.object({
  token: z.string().describe('Unguessable 192-bit base64url token; the only key to the shared itinerary'),
  enabledAt: z.string().describe('ISO timestamp the current link was created'),
  referralCode: z
    .string()
    .nullish()
    .describe("Owner's referral code, copied at enable time so the public page never reads the user profile"),
});
export type TripShareLink = z.infer<typeof TripShareLinkSchema>;

/**
 * The canonical `ModelSpec` for the trips collection.
 * It defines the path (`trips/{id}`) and the data shape for a trip document.
 * Trips represent travel planning entities that can have multiple chats.
 */
export const TripModelSpec = defineModelSpec({
  path: collectionPath`trips/${id}`,
  shape: {
    userId: z.string(),
    title: z.string(),
    participants: z.array(ParticipantSchema),
    status: z.enum(['active', 'archived']).default('active'),
    destinationIds: z
      .array(z.string())
      .default([])
      .describe('Array of destination IDs (references to DestinationModel)'),
    destinationPlan: z
      .array(
        z.object({
          destinationId: z.string(),
          name: z.string().nullish(),
          startDate: z.string().nullish().describe('YYYY-MM-DD'),
          endDate: z.string().nullish().describe('YYYY-MM-DD'),
        }),
      )
      .optional()
      .describe(
        'Ordered per-destination plan with date ranges, synced from working memory. Lets the client group the itinerary by destination before any hotel/flight is booked. Never written by clients.',
      ),
    startDate: z.string().nullish().optional().describe('Trip start date in ISO format'),
    endDate: z.string().nullish().optional().describe('Trip end date in ISO format'),
    isDemo: z.boolean().optional().describe('Whether this trip was seeded with demo data during onboarding'),
    demoDataReady: z.boolean().optional().describe('Set to true once all demo data has been seeded'),
    archivedAt: zFirestampOptional(),
    itineraryDaySummaries: z
      .record(z.string(), DaySummarySchema)
      .optional()
      .describe('AI-generated day titles keyed by date (yyyy-MM-dd); regenerated when itinerary or trip dates change'),
    itinerarySummariesVersion: z
      .string()
      .optional()
      .describe('Hash of trip start/end dates + all itinerary item ids and updatedAt; used to decide when to regenerate day summaries'),
    itinerarySummary: z
      .string()
      .optional()
      .describe('AI-generated one- or two-sentence summary of the whole trip; regenerated with day summaries'),
    shareLink: TripShareLinkSchema.nullish().describe('Public itinerary share link; absent when sharing is off. Never write from clients.'),
    derived: TripDerivedSchema.optional().describe('Auto-maintained admin-list fields. Never write directly.'),
  },
  constraints: [orderBy('createdAt', 'desc')],
});
export type TripModelSpec = typeof TripModelSpec;
